/**
 * Disallow lists for command arguments and environment variables
 * per game engine / operating system combination.
 *
 * These are managed by the platform — users cannot override or set these
 * values because they are controlled by the orchestrator at launch time.
 */

export type EngineOS = {
  gameEngine?: 'unreal' | 'unity' | null;
  operatingSystem?: 'windows' | 'linux' | null;
};

interface DisallowEntry {
  /** Prefix or exact match for the arg/env key */
  pattern: string;
  /** Shown in the UI when a user tries to add a disallowed item */
  reason: string;
}

interface DisallowRule {
  gameEngine?: 'unreal' | 'unity';
  operatingSystem?: 'windows' | 'linux';
  disallowedArgs: DisallowEntry[];
  disallowedEnvVars: DisallowEntry[];
}

const rules: DisallowRule[] = [
  // ── Unreal Engine (all platforms) ──────────────────────────────
  {
    gameEngine: 'unreal',
    disallowedArgs: [
      { pattern: '-PixelStreamingURL', reason: 'Set by orchestrator' },
      { pattern: '-PixelStreamingPort', reason: 'Set by orchestrator' },
      { pattern: '-PixelStreamingIP', reason: 'Set by orchestrator' },
      { pattern: '-RenderOffscreen', reason: 'Set by orchestrator' },
      { pattern: '-Unattended', reason: 'Set by orchestrator' },
      { pattern: '-GraphicsAdapter', reason: 'Set by orchestrator' },
    ],
    disallowedEnvVars: [],
  },

  // ── Unity (all platforms) ──────────────────────────────────────
  {
    gameEngine: 'unity',
    disallowedArgs: [
      { pattern: '-port', reason: 'Set by orchestrator' },
      { pattern: '-signaling-url', reason: 'Set by orchestrator' },
    ],
    disallowedEnvVars: [],
  },

  // ── Linux-specific (all engines) ──────────────────────────────
  {
    operatingSystem: 'linux',
    disallowedArgs: [],
    disallowedEnvVars: [
      { pattern: 'DISPLAY', reason: 'Set by orchestrator' },
    ],
  },
];

/**
 * Check whether a command argument is disallowed for the given engine/OS.
 * Matching is case-insensitive prefix: `-PixelStreamingURL=foo` matches
 * the pattern `-PixelStreamingURL`.
 *
 * Returns the reason string if disallowed, or null if allowed.
 */
export function isArgDisallowed(arg: string, ctx: EngineOS): string | null {
  const lower = arg.toLowerCase();
  for (const rule of rules) {
    if (!ruleApplies(rule, ctx)) continue;
    for (const entry of rule.disallowedArgs) {
      if (lower.startsWith(entry.pattern.toLowerCase())) {
        return entry.reason;
      }
    }
  }
  return null;
}

/**
 * Check whether an environment variable key is disallowed for the given engine/OS.
 * Matching is exact (case-insensitive on the key).
 *
 * Returns the reason string if disallowed, or null if allowed.
 */
export function isEnvVarDisallowed(key: string, ctx: EngineOS): string | null {
  const upper = key.toUpperCase();
  for (const rule of rules) {
    if (!ruleApplies(rule, ctx)) continue;
    for (const entry of rule.disallowedEnvVars) {
      if (upper === entry.pattern.toUpperCase()) {
        return entry.reason;
      }
    }
  }
  return null;
}

function ruleApplies(rule: DisallowRule, ctx: EngineOS): boolean {
  if (rule.gameEngine && rule.gameEngine !== ctx.gameEngine) return false;
  if (rule.operatingSystem && rule.operatingSystem !== ctx.operatingSystem) return false;
  return true;
}
